import { useUserSubscriptionState } from "@/hooks/useUserSubscriptionState";
import { useAuth } from "@/lib/AuthContext";
import { readMembershipSource } from "@/lib/membershipSource";
import { useAppleTrial } from "@/lib/purchases/useAppleTrial";
import { isSubscriptionRowOnTrial } from "@/lib/trialCourseCta";
import {
  mixpanelService,
  type MixpanelSubscriptionTraits,
} from "@/services/mixpanel.service";
import { useEffect, useRef } from "react";

/**
 * Pushes subscription traits (On Trial, Membership Tier, Subscription Status,
 * Billing Interval, Trial End At, Join Date) to Mixpanel whenever they change,
 * so cohorts like "trialing vs non-trialing users who viewed course X" can be
 * built. On Trial covers both WooCommerce trials (subscription row) and Apple
 * trials (RevenueCat entitlement on iOS). Renders nothing; mount once inside
 * AuthProvider + QueryClientProvider.
 */
export function MixpanelSubscriptionTraitsSync() {
  const { user, membershipTier, membershipLoading, preferenceMetadata } = useAuth();
  const userId = user && !user.is_anonymous ? user.id : undefined;
  const { data: row = null, isSuccess } = useUserSubscriptionState({ userId });
  const appleTrial = useAppleTrial(userId);
  const lastSentRef = useRef<string | null>(null);

  useEffect(() => {
    if (!userId || !isSuccess || membershipLoading || !appleTrial.resolved) return;

    const rowOnTrial = isSubscriptionRowOnTrial(row, user?.created_at ?? null);
    const traits: MixpanelSubscriptionTraits = {
      onTrial: rowOnTrial || appleTrial.onTrial,
      trialEndAt: row?.trial_end_at ?? appleTrial.trialEndAt ?? null,
      membershipTier,
      membershipSource: readMembershipSource(preferenceMetadata),
      subscriptionStatus:
        row?.subscription_status ?? (appleTrial.onTrial ? "active" : null),
      billingInterval: row?.billing_interval ?? null,
      joinDate: user?.created_at ?? null,
    };

    const key = `${userId}|${JSON.stringify(traits)}`;
    if (lastSentRef.current === key) return;
    lastSentRef.current = key;

    void mixpanelService.setSubscriptionTraits(traits, user);
  }, [userId, user, row, isSuccess, membershipTier, membershipLoading, appleTrial, preferenceMetadata]);

  return null;
}
